import { useRef, useState, type DragEvent } from "react";
import type { Progress } from "../../api/delivery";

/** Step 1 and 2 of an upload: pick the contract, drop or choose the PDFs, and
 * send them to be read. While they are read the card shows the server's own
 * progress, file by file, from the poller. */

function isPdf(file: File): boolean {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

function size(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ChooseCard({
  contracts,
  contract,
  onContract,
  files,
  onFiles,
  onStart,
  busy,
}: {
  contracts: readonly { id: number; name: string; code: string }[];
  contract: string;
  onContract: (id: string) => void;
  files: readonly File[];
  onFiles: (files: File[]) => void;
  onStart: () => void;
  busy: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [skipped, setSkipped] = useState(0);

  function take(chosen: File[]) {
    const pdfs = chosen.filter(isPdf);
    setSkipped(chosen.length - pdfs.length);
    onFiles([...files, ...pdfs.filter((f) => !files.some((had) => had.name === f.name && had.size === f.size))]);
  }

  function onDrop(event: DragEvent) {
    event.preventDefault();
    setOver(false);
    take(Array.from(event.dataTransfer.files));
  }

  return (
    <section className="card card-pad upload-card">
      <label className="upload-contract">
        Contract
        <select className="select" value={contract} onChange={(e) => onContract(e.target.value)}>
          <option value="">Choose…</option>
          {contracts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <input
        ref={input}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        hidden
        onChange={(e) => {
          take(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
      <div
        className={over ? "upload-drop over" : "upload-drop"}
        role="button"
        tabIndex={0}
        aria-label="Choose QCP PDFs"
        onClick={() => input.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            input.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        <strong>Drop QCP PDFs here</strong>
        <span>or click to choose · as many as you like</span>
      </div>
      {skipped > 0 ? (
        <p className="notice warn">
          {skipped} file{skipped === 1 ? " was" : "s were"} not a PDF and {skipped === 1 ? "was" : "were"} left out.
        </p>
      ) : null}
      {files.length > 0 ? (
        <>
          <ul className="upload-files">
            {files.map((file) => (
              <li key={`${file.name}-${file.size}`}>
                <span className="mono upload-file-name">{file.name}</span>
                <span className="upload-file-size">{size(file.size)}</span>
                <button
                  type="button"
                  className="go-link"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => onFiles(files.filter((f) => f !== file))}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
          <div className="upload-ready">
            <span>
              {files.length} file{files.length === 1 ? "" : "s"} ready
            </span>
            {contract === "" ? <span className="upload-hint">Choose the contract first.</span> : null}
            <button type="button" className="btn btn-primary" disabled={busy || contract === ""} onClick={onStart}>
              Upload and read
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}

/** Step 2: the files being read. Before the first poll answers, the count is
 * the files sent, so the bar never starts from nothing. */
export function ReadingCard({ progress, sent }: { progress: Progress | undefined; sent: number }) {
  const total = progress?.files_total || sent;
  const done = progress?.files_done ?? 0;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <section className="card card-pad upload-card" aria-live="polite">
      <div className="upload-reading-head">
        <strong>
          Reading {done} of {total}
        </strong>
        <span>{progress?.phase_label ?? "Sending the files"}</span>
        {progress?.files_failed ? <span className="upload-failed">{progress.files_failed} failed</span> : null}
      </div>
      <div className="meter upload-meter" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
        <span style={{ width: `${pct}%` }} />
      </div>
      {progress && progress.files.length > 0 ? (
        <ul className="upload-files">
          {progress.files.map((f) => (
            <li key={f.filename} className={f.failed ? "failed" : f.done ? "done" : undefined}>
              <span className="mono upload-file-name">{f.filename}</span>
              <span className="upload-file-size">
                {f.failed ? "failed" : f.done ? "read" : f.step ? (progress.step_labels[f.step] ?? f.step) : "waiting"}
                {f.detail ? ` · ${f.detail}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
